import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  IconButton,
  styled,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import {
  ArrowBackIosNewIcon,
  PersonAddAltIcon,
  PlaylistAddIcon,
  SearchIcon,
} from 'assets/icons';
import { useHeaderContext } from 'components/providers';
import { PAGE_LIST } from 'constant/pages';
import { ReactNode, useEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import ja from 'utils/ja';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  height: '80px',
  [theme.breakpoints.down('sm')]: {
    height: '60px',
  },
  width: '100%',
  backgroundColor: theme.palette.common.white,
  boxShadow: 'none',
  ...(open && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface IAppBarProps {
  open: boolean;
  handleDrawerToggle: () => void;
}

interface AppBarItemType {
  type: string;
  title: string;
  path: string;
  iconAdd?: ReactNode;
  clickAdd?: () => void;
}

const AppBar: React.FC<IAppBarProps> = ({ open, handleDrawerToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const appBarItems: AppBarItemType[] = [
    {
      type: 'list',
      title: ja.home.title,
      path: PAGE_LIST.HOME.pathname(),
      iconAdd: (
        <PersonAddAltIcon
          sx={{ fill: '#F3C623', fontSize: { xs: '38px', sm: '70px' } }}
        />
      ),
      clickAdd: () => navigate(PAGE_LIST.USER_ADD.pathname()),
    },
    {
      type: 'list',
      title: ja.tag.title,
      path: PAGE_LIST.TAG.pathname(),
      iconAdd: (
        <PlaylistAddIcon
          sx={{ fill: '#F3C623', fontSize: { xs: '38px', sm: '70px' } }}
        />
      ),
      clickAdd: () => setStateClick({ click: true, type: 'add' }),
    },
    {
      type: 'detail',
      title: ja.user_add.title,
      path: PAGE_LIST.USER_ADD.pathname(),
    },
    {
      type: 'detail',
      title: ja.user_detail.title,
      path: PAGE_LIST.USER_DETAIL.pathname(),
    },
  ];
  const currentPath = location.pathname;

  const { setStateClick } = useHeaderContext();

  const [currentPage, setCurrentPage] = useState<AppBarItemType>();

  useEffect(() => {
    setCurrentPage(
      appBarItems.find((item) => matchPath(item.path, currentPath))
    );
  }, [currentPath]);

  return (
    <StyledAppBar open={open}>
      <Toolbar
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
        disableGutters
      >
        {currentPage?.type === 'detail' && (
          <IconButton
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowBackIosNewIcon
              sx={{
                fill: theme.palette.primary.main,
                fontSize: { xs: '30px', sm: '50px' },
              }}
            />
          </IconButton>
        )}
        {currentPage?.type === 'list' && (
          <IconButton onClick={handleDrawerToggle}>
            <MenuIcon
              sx={{
                fill: theme.palette.primary.main,
                fontSize: { xs: '38px', sm: '70px' },
              }}
            />
          </IconButton>
        )}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '18px', sm: '35px' },
            color: theme.palette.common.black,
          }}
        >
          {currentPage?.title}
        </Typography>
        {currentPage?.type === 'list' && (
          <Box
            sx={{
              position: 'absolute',
              right: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton
              onClick={() => setStateClick({ click: true, type: 'search' })}
            >
              <SearchIcon
                sx={{ fill: '#06923E', fontSize: { xs: '38px', sm: '70px' } }}
              />
            </IconButton>
            <IconButton onClick={currentPage.clickAdd}>
              {currentPage.iconAdd}
            </IconButton>
          </Box>
        )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default AppBar;
